import { generateInitials } from "@shared/utils/textFormatting";
import React from "react";

interface Assignee {
  _id: string;
  name: string;
}

interface WorkOrderCardAssigneesProps {
  assignees: Assignee[];
  /** Maximum number of avatars to show before +N */
  maxVisible?: number;
}

const AVATAR_CHIP_CLASS =
  "relative flex items-center justify-center rounded-full select-none w-2xl h-2xl bg-brand-lightest border-2 border-solid border-inverse";

const INITIALS_CLASS =
  "text-brand text-2xs font-bold uppercase leading-none";

/**
 * WorkOrderCardAssignees
 *
 * Displays assignee avatars with overflow indicator.
 * Hovering anywhere on the group shows the full list of assignee names.
 */
export const WorkOrderCardAssignees: React.FC<WorkOrderCardAssigneesProps> = ({
  assignees,
  maxVisible = 3,
}) => {
  if (!assignees.length) return null;

  const visibleAssignees = assignees.slice(0, maxVisible);
  const remainingCount = assignees.length - maxVisible;
  const allNames = assignees.map((a) => a.name).join(", ");

  return (
    <div
      className="flex items-center -space-x-xs"
      title={allNames}
      aria-label={allNames}
    >
      {visibleAssignees.map((assignee, index) => (
        <div
          key={assignee._id}
          className={AVATAR_CHIP_CLASS}
          style={{ zIndex: maxVisible - index }}
        >
          <span className={INITIALS_CLASS}>
            {generateInitials(assignee.name)}
          </span>
        </div>
      ))}
      {remainingCount > 0 && (
        <div className={AVATAR_CHIP_CLASS} style={{ zIndex: 0 }}>
          <span className={INITIALS_CLASS}>+{remainingCount}</span>
        </div>
      )}
    </div>
  );
};
